import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Divider, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Stack
} from '@mui/material';
import { useTheme, darken, lighten, alpha } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { setStartDate, setTotalSprints, setSprintOverride } from '../../store/slices/sprintsSlice';
import type { WorkstreamSprintStatus } from '../../store/slices/sprintsSlice';
import { reorderEpics, getEffectiveStoryPoints } from '../../store/slices/epicsSlice';
import ModalActionFooter from '../../components/ModalActionFooter/ModalActionFooter';
import NumberInput from '../../components/NumberInput/NumberInput';

// Helper: add business days to a date
const addBusinessDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const dow = result.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return result;
};

const getSprintDates = (startDate: Date, sprintLength: number, totalSprints: number) => {
  const sprints: { start: Date; end: Date }[] = [];
  let current = new Date(startDate);
  for (let i = 0; i < totalSprints; i++) {
    const end = addBusinessDays(current, sprintLength - 1);
    sprints.push({ start: new Date(current), end: new Date(end) });
    current = addBusinessDays(end, 1);
  }
  return sprints;
};

const formatDate = (d: Date) =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const SPRINT_CARD_MIN_WIDTH = 80;
const SPRINT_CARD_GAP = 4;
// Width of the settings IconButton that sits to the left of the month header strip.
const SETTINGS_ICON_WIDTH = 40;
const MS_PER_DAY = 86_400_000;

interface SprintModalProps {
  open: boolean;
  workstreamId: string;
  sprintIndex: number;
  sprintDates: { start: Date; end: Date };
  defaultVelocity: number;
  overrideData?: { velocity: number };
  onSave: (velocity: number) => void;
  onClose: () => void;
}

const SprintModal: React.FC<SprintModalProps> = ({
  open, sprintIndex, sprintDates, defaultVelocity, overrideData, onSave, onClose,
}) => {
  const [velocity, setVelocity] = useState(overrideData?.velocity ?? defaultVelocity);

  React.useEffect(() => {
    setVelocity(overrideData?.velocity ?? defaultVelocity);
  }, [open, overrideData, defaultVelocity]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Sprint {sprintIndex + 1}: {formatDate(sprintDates.start)} - {formatDate(sprintDates.end)}
          </Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Velocity Override</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NumberInput
              value={velocity}
              onChange={setVelocity}
              min={0}
            />
            <Typography variant="caption" color="text.secondary">pts (default: {defaultVelocity})</Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <ModalActionFooter
          onCancel={onClose}
          onSave={() => onSave(velocity)}
        />
      </DialogActions>
    </Dialog>
  );
};

const WorkstreamForecaster: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const pods = useAppSelector((state) => state.pods.pods);
  const workstreams = useAppSelector((state) => state.workstreams.workstreams);
  const allEpics = useAppSelector((state) => state.epics.epics);
  const categories = useAppSelector((state) => state.categories.categories);
  const sprintsConfig = useAppSelector((state) => state.sprints);
  const workstreamSprints = useAppSelector((state) => state.sprints.workstreamSprints);

  const [timelineModalOpen, setTimelineModalOpen] = useState(false);
  const [newStartDate, setNewStartDate] = useState('');
  const [newTotalSprints, setNewTotalSprints] = useState(sprintsConfig.totalSprints);
  const [sprintModal, setSprintModal] = useState<{
    open: boolean; workstreamId: string; sprintIndex: number;
  }>({ open: false, workstreamId: '', sprintIndex: 0 });
  const [hoveredEpic, setHoveredEpic] = useState<{ workstreamId: string; epicId: string } | null>(null);
  const [expandedWorkstreams, setExpandedWorkstreams] = useState<Record<string, boolean>>({});

  const theme = useTheme();
  const getOutlineColor = (fillColor: string) =>
    theme.palette.mode === 'dark' ? lighten(fillColor, 0.4) : darken(fillColor, 0.3);

  const getEpicsForWorkstream = (workstreamId: string) =>
    allEpics.filter((e) => e.workstreamId === workstreamId).sort((a, b) => a.priority - b.priority);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;
    const sourceWsId = source.droppableId;
    const destWsId = destination.droppableId;

    if (sourceWsId === destWsId) {
      const wsEpics = getEpicsForWorkstream(sourceWsId);
      const items = Array.from(wsEpics);
      const [removed] = items.splice(source.index, 1);
      items.splice(destination.index, 0, removed);
      const reordered = items.map((e, i) => ({ ...e, priority: i }));
      dispatch(reorderEpics({ workstreamId: sourceWsId, epics: reordered }));
    } else {
      const epicId = result.draggableId;
      const movedEpic = allEpics.find((e) => e.id === epicId);
      if (!movedEpic) return;

      const updatedSourceEpics = getEpicsForWorkstream(sourceWsId)
        .filter((e) => e.id !== epicId)
        .map((e, i) => ({ ...e, priority: i }));

      const destEpics = getEpicsForWorkstream(destWsId);
      const newDestEpics = [...destEpics];
      newDestEpics.splice(destination.index, 0, { ...movedEpic, workstreamId: destWsId });
      const updatedDestEpics = newDestEpics.map((e, i) => ({ ...e, priority: i }));

      dispatch(reorderEpics({ workstreamId: sourceWsId, epics: updatedSourceEpics }));
      dispatch(reorderEpics({ workstreamId: destWsId, epics: updatedDestEpics }));
    }
  };

  const handleEpicMouseEnter = (workstreamId: string, epicId: string) => {
    setHoveredEpic({ workstreamId, epicId });
  };

  const handleEpicMouseLeave = () => {
    setHoveredEpic(null);
  };

  const toggleWorkstreamExpanded = (workstreamId: string) => {
    setExpandedWorkstreams((prev) => ({ ...prev, [workstreamId]: !prev[workstreamId] }));
  };

  const startDate = new Date(sprintsConfig.startDate);
  const sprintDates = getSprintDates(startDate, sprintsConfig.sprintLength, sprintsConfig.totalSprints);

  /**
   * Get the default velocity for a workstream for a given sprint index.
   * Sums the defaultVelocity of all pods assigned to that sprint via workstream entries.
   * Falls back to the last entry's pod velocity if sprintIndex exceeds entry count.
   */
  const getWorkstreamSprintVelocity = (workstreamId: string, sprintIndex: number): number => {
    const ws = workstreams.find((w) => w.id === workstreamId);
    if (!ws || ws.entries.length === 0) return 0;
    const entry = ws.entries[sprintIndex] ?? ws.entries[ws.entries.length - 1];
    return entry.podIds.reduce((sum, podId) => {
      const pod = pods.find((p) => p.id === podId);
      return sum + (pod?.defaultVelocity ?? 0);
    }, 0);
  };

  const getOverride = (workstreamId: string, sprintIndex: number) =>
    sprintsConfig.overrides.find((o) => o.workstreamId === workstreamId && o.sprintIndex === sprintIndex);

  const getVelocity = (workstreamId: string, sprintIndex: number) => {
    const override = getOverride(workstreamId, sprintIndex);
    return override?.velocity ?? getWorkstreamSprintVelocity(workstreamId, sprintIndex);
  };

  /** Returns the official WorkstreamSprint for a given sprint slot, or undefined if none. */
  const getOfficialSprint = (workstreamId: string, sprintIndex: number) =>
    workstreamSprints.find(
      (s) => s.workstreamId === workstreamId && s.number === sprintIndex + 1,
    );

  /** Returns the WorkstreamSprintStatus for a given sprint slot, or null if not an official sprint. */
  const getSprintStatus = (workstreamId: string, sprintIndex: number): WorkstreamSprintStatus | null => {
    const ws = workstreamSprints.find(
      (s) => s.workstreamId === workstreamId && s.number === sprintIndex + 1,
    );
    return ws?.status ?? null;
  };
  // Check if a workstream's total epic story points exceed the total sprint velocity
  const capacityExceedsSprints = useMemo(() => {
    const result: Record<string, boolean> = {};
    workstreams.forEach((ws) => {
      const wsEpics = allEpics.filter((e) => e.workstreamId === ws.id);
      const totalPoints = wsEpics.reduce((sum, e) => sum + getEffectiveStoryPoints(e, 'Engineering'), 0);
      const totalVelocity = sprintDates.reduce((sum, _, si) => sum + getVelocity(ws.id, si), 0);
      result[ws.id] = totalPoints > totalVelocity;
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workstreams, allEpics, sprintsConfig, pods]);

  // Calculate epic burn layout for a workstream
  const calcEpicBurndown = (workstreamId: string) => {
    const wsEpics = allEpics.filter((e) => e.workstreamId === workstreamId).sort((a, b) => a.priority - b.priority);
    const result: {
      epicId: string;
      title: string;
      color: string;
      progressPct: number;
      sprintSegments: { sprintIndex: number; pts: number; startPct: number; widthPct: number; progressFillPct: number }[];
    }[] = [];

    const totalPtsByEpic: Record<string, number> = {};
    const remainingByEpic: Record<string, number> = {};
    wsEpics.forEach((e) => {
      const pts = getEffectiveStoryPoints(e, 'Engineering');
      totalPtsByEpic[e.id] = pts;
      remainingByEpic[e.id] = pts;
    });

    for (let si = 0; si < sprintsConfig.totalSprints; si++) {
      const velocity = getVelocity(workstreamId, si);
      // Skip sprints with zero velocity (no pods assigned) to avoid division by zero
      // when computing widthPct below.
      if (velocity === 0) continue;
      let remaining = velocity;
      let usedPct = 0;

      for (const epic of wsEpics) {
        if (remaining <= 0) break;
        if (remainingByEpic[epic.id] <= 0) continue;

        const burn = Math.min(remaining, remainingByEpic[epic.id]);
        const widthPct = (burn / velocity) * 100;

        const existingResult = result.find((r) => r.epicId === epic.id);
        const cat = categories.find((c) => c.id === epic.categoryId);
        const color = cat?.color || '#9e9e9e';
        const segment = { sprintIndex: si, pts: burn, startPct: usedPct, widthPct, progressFillPct: 0 };

        if (existingResult) {
          existingResult.sprintSegments.push(segment);
        } else {
          const stories = epic.stories ?? [];
          const totalCount = stories.length;
          const closedCount = stories.filter((s) => s.parentState === 'Closed').length;
          const progressPct = totalCount > 0 ? Math.round((closedCount / totalCount) * 100) : 0;
          result.push({
            epicId: epic.id,
            title: epic.title,
            color,
            progressPct,
            sprintSegments: [segment],
          });
        }

        remainingByEpic[epic.id] -= burn;
        remaining -= burn;
        usedPct += widthPct;
      }
    }
    // Compute per-segment progress fill so that progress continues across sprints
    for (const epicEntry of result) {
      const totalPts = totalPtsByEpic[epicEntry.epicId] ?? 0;
      const completedPts = totalPts > 0 ? (totalPts * epicEntry.progressPct / 100) : 0;
      let cumulativePts = 0;
      for (const seg of epicEntry.sprintSegments) {
        if (totalPts === 0 || completedPts <= cumulativePts) {
          seg.progressFillPct = 0;
        } else if (completedPts >= cumulativePts + seg.pts) {
          seg.progressFillPct = 100;
        } else {
          seg.progressFillPct = ((completedPts - cumulativePts) / seg.pts) * 100;
        }
        cumulativePts += seg.pts;
      }
    }
    return result;
  };

  // Get month headers with flex values proportional to the calendar days each sprint
  // contributes to that month.  Each whole sprint = flexValue 1; sprints that cross
  // a month boundary are split so the visual boundary in the header row aligns with
  // the sprint card below.  Using flex values (not pixel widths) keeps the layout
  // fluid so month headers resize correctly when the browser window is resized.
  const getMonthHeaders = () => {
    const months: { label: string; flexValue: number }[] = [];

    sprintDates.forEach((sd) => {
      const startLabel = sd.start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const endLabel = sd.end.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

      if (startLabel === endLabel) {
        // Sprint is fully within one month
        const entry = months.find((m) => m.label === startLabel);
        if (entry) { entry.flexValue += 1; }
        else { months.push({ label: startLabel, flexValue: 1 }); }
      } else {
        // Sprint spans a month boundary – split proportionally by calendar days.
        // Normalise to local midnight so time-of-day in stored ISO strings doesn't skew fractions.
        const startDay = new Date(sd.start.getFullYear(), sd.start.getMonth(), sd.start.getDate());
        const endDay = new Date(sd.end.getFullYear(), sd.end.getMonth(), sd.end.getDate());
        const boundary = new Date(startDay.getFullYear(), startDay.getMonth() + 1, 1);
        const totalMs = endDay.getTime() - startDay.getTime() + MS_PER_DAY; // inclusive
        const startFrac = (boundary.getTime() - startDay.getTime()) / totalMs;
        const endFrac = 1 - startFrac;

        const startEntry = months.find((m) => m.label === startLabel);
        if (startEntry) { startEntry.flexValue += startFrac; }
        else { months.push({ label: startLabel, flexValue: startFrac }); }

        const endEntry = months.find((m) => m.label === endLabel);
        if (endEntry) { endEntry.flexValue += endFrac; }
        else { months.push({ label: endLabel, flexValue: endFrac }); }
      }
    });
    return months;
  };

  const monthHeaders = getMonthHeaders();

  // Minimum pixel width of the sprint card row (N cards * min-width + gaps).
  // Used to prevent pod containers and month headers from shrinking past this point.
  const sprintRowMinWidth = useMemo(
    () => sprintDates.length * SPRINT_CARD_MIN_WIDTH + Math.max(0, sprintDates.length - 1) * SPRINT_CARD_GAP,
    [sprintDates.length],
  );

  const handleSaveSprintOverride = (velocity: number) => {
    dispatch(setSprintOverride({ workstreamId: sprintModal.workstreamId, sprintIndex: sprintModal.sprintIndex, velocity, memberCapacities: {} }));
    setSprintModal({ open: false, workstreamId: '', sprintIndex: 0 });
  };

  const handleSaveTimeline = () => {
    if (newStartDate) dispatch(setStartDate(new Date(newStartDate).toISOString()));
    dispatch(setTotalSprints(newTotalSprints));
    setTimelineModalOpen(false);
  };

  return (
    <Box>
      {workstreams.length === 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Tooltip title="Configure Timeline">
            <IconButton onClick={() => { setNewStartDate(''); setNewTotalSprints(sprintsConfig.totalSprints); setTimelineModalOpen(true); }}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Typography color="text.secondary">No workstreams configured. Please set up workstreams first.</Typography>
        </Box>
      )}
      {/* Month headers */}
      {workstreams.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: sprintRowMinWidth + SETTINGS_ICON_WIDTH }}>
            {/* Month timeline */}
            <Box sx={{ display: 'flex', mb: 1, pl: 0, alignItems: 'center' }}>
              <Tooltip title="Configure Timeline">
                <IconButton onClick={() => { setNewStartDate(''); setNewTotalSprints(sprintsConfig.totalSprints); setTimelineModalOpen(true); }}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
              <Box sx={{ display: 'flex', flex: 1 }}>
                {monthHeaders.map((m, i) => (
                  <Box
                    key={i}
                    sx={{
                      flex: m.flexValue,
                      bgcolor: 'grey.500',
                      color: 'primary.contrastText',
                      textAlign: 'center',
                      py: 0.5,
                      borderRadius: 1,
                      borderRight: i < monthHeaders.length - 1 ? '2px solid' : 'none',
                      borderColor: 'background.default',
                      overflow: 'hidden',
                    }}
                  >
                    <Tooltip title={m.label} placement="bottom">
                      <Typography variant="caption" fontWeight={600} noWrap sx={{ display: 'block' }}>{m.label}</Typography>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            </Box>
            {/* Workstream rows */}
            <DragDropContext onDragEnd={handleDragEnd}>
              {workstreams.map((ws) => {
                const burndown = calcEpicBurndown(ws.id);
                return (
                  <Paper key={ws.id} elevation={2} sx={{ mb: 3, p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Tooltip
                        title={ws.description || ''}
                        arrow
                        placement="top"
                        disableHoverListener={!ws.description}
                        disableFocusListener={!ws.description}
                        disableTouchListener={!ws.description}
                      >
                        <Chip
                          label={ws.name}
                          variant="outlined"
                          clickable
                          onClick={() => navigate(`/epic-planner/workstreams/${ws.id}/edit`)}
                          aria-label={`Edit workstream ${ws.name}`}
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 700,
                            height: 'auto',
                            py: 0.75,
                            borderWidth: 2,
                            cursor: 'pointer',
                            '& .MuiChip-label': { px: 1.5 },
                            '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                          }}
                        />
                      </Tooltip>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {capacityExceedsSprints[ws.id] && (
                          <Tooltip title="Total epic story points exceed the capacity available across the displayed sprints. Some epics may not be fully shown.">
                            <Chip label="Attention" size="small" variant="outlined" color="warning" />
                          </Tooltip>
                        )}
                        <Tooltip title={expandedWorkstreams[ws.id] ? 'Hide Epics' : 'Show Epics'}>
                          <IconButton size="small" onClick={() => toggleWorkstreamExpanded(ws.id)}>
                            {expandedWorkstreams[ws.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {/* Sprint cards row */}
                    <Box sx={{ display: 'flex', gap: `${SPRINT_CARD_GAP}px`, mb: 1 }}>
                      {sprintDates.map((sd, si) => {
                        const velocity = getVelocity(ws.id, si);
                        const override = getOverride(ws.id, si);
                        const officialSprint = getOfficialSprint(ws.id, si);
                        const isOfficial = officialSprint !== undefined;
                        const sprintStatus = getSprintStatus(ws.id, si);
                        const isClosed = sprintStatus === 'Closed';
                        const isActive = sprintStatus === 'Active';

                        const handleSprintCardClick = () => {
                          if (officialSprint) {
                            navigate(
                              `/epic-planner/workstreams/${ws.id}/sprints/${officialSprint.id}/edit`,
                              { state: { from: '/workstream-forecaster' } },
                            );
                          } else {
                            setSprintModal({ open: true, workstreamId: ws.id, sprintIndex: si });
                          }
                        };

                        // Border style per status
                        const borderStyle = isClosed
                          ? '1px dashed'
                          : isActive
                            ? '2px solid'
                            : isOfficial
                              ? '2px solid'
                              : '1px solid';
                        const borderColor = isClosed
                          ? (theme.palette.mode === 'dark' ? 'grey.600' : 'grey.400')
                          : isActive
                            ? 'primary.main'
                            : isOfficial
                              ? 'primary.main'
                              : 'divider';

                        // Background per status
                        const bgColor = isClosed
                          ? (theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.black, 0.2)
                            : alpha(theme.palette.grey[500], 0.08))
                          : isActive
                            ? alpha(theme.palette.primary.main, 0.06)
                            : 'background.paper';

                        // Box shadow for active sprint — subtle glow
                        const boxShadow = isActive
                          ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.18)}`
                          : 'none';

                        return (
                          <Box
                            key={si}
                            onClick={handleSprintCardClick}
                            sx={{
                              flex: 1,
                              minWidth: SPRINT_CARD_MIN_WIDTH,
                              border: borderStyle,
                              borderColor,
                              borderRadius: 1,
                              p: 0.5,
                              position: 'relative',
                              bgcolor: bgColor,
                              boxShadow,
                              minHeight: 60,
                              opacity: isClosed ? 0.72 : 1,
                              transition: 'box-shadow 0.15s ease, opacity 0.15s ease',
                              '&:hover': {
                                bgcolor: isClosed
                                  ? (theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.common.black, 0.28)
                                    : alpha(theme.palette.grey[500], 0.14))
                                  : 'action.hover',
                                opacity: isClosed ? 0.88 : 1,
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                width: '100%',
                                textAlign: 'left',
                                p: 0.5,
                              }}
                            >
                              <Tooltip title={`${formatDate(sd.start)} - ${formatDate(sd.end)}`} placement="top">
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  sx={{
                                    cursor: 'default',
                                    color: isClosed ? 'text.disabled' : isActive ? 'primary.main' : 'text.primary',
                                  }}
                                >
                                  Sprint {si + 1}
                                </Typography>
                              </Tooltip>

                            </Box>
                            <Chip
                              label={`${velocity} pts`}
                              size="small"
                              color={override ? 'warning' : 'default'}
                              variant={override ? 'outlined' : 'filled'}
                              sx={{
                                mt: 0.5,
                                fontSize: '0.65rem',
                                opacity: isClosed ? 0.6 : 1,
                              }}
                            />
                            {/* Epic burn overlay */}
                            <Box sx={{ display: 'flex', gap: '2px', mt: 1, height: 48, borderRadius: 0.5, overflow: 'hidden', bgcolor: theme.palette.mode === 'dark' ? 'background.default' : 'grey.200', opacity: isClosed ? 0.5 : 1 }}>
                              {burndown
                                .flatMap((b) => b.sprintSegments.filter((s) => s.sprintIndex === si).map((s) => ({
                                  color: b.color,
                                  title: b.title,
                                  epicId: b.epicId,
                                  widthPct: s.widthPct,
                                  progressFillPct: s.progressFillPct,
                                })))
                                .map((seg, idx) => {
                                  const isHoveredEpic = hoveredEpic?.workstreamId === ws.id && hoveredEpic?.epicId === seg.epicId;
                                  return (
                                    <Tooltip key={idx} title={`${seg.title} — ${seg.widthPct.toFixed(0)}% of Sprint ${si + 1} · Click to edit`} placement="top">
                                      <Box
                                        role="button"
                                        tabIndex={0}
                                        onMouseEnter={() => handleEpicMouseEnter(ws.id, seg.epicId)}
                                        onMouseLeave={handleEpicMouseLeave}
                                        onClick={(e) => { e.stopPropagation(); navigate(`/epic-planner/${seg.epicId}/edit`, { state: { from: '/workstream-forecaster' } }); }}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); navigate(`/epic-planner/${seg.epicId}/edit`, { state: { from: '/workstream-forecaster' } }); } }}
                                        sx={{
                                          width: `${seg.widthPct}%`,
                                          bgcolor: isClosed ? alpha(seg.color, 0.55) : seg.color,
                                          height: '100%',
                                          boxShadow: isHoveredEpic ? `inset 0 0 0 2px ${getOutlineColor(seg.color)}` : 'none',
                                          cursor: 'pointer',
                                          position: 'relative',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        {seg.progressFillPct > 0 && (
                                          <Box
                                            sx={{
                                              position: 'absolute',
                                              top: 0,
                                              bottom: 0,
                                              padding: '2px',
                                              margin: '6px',
                                              width: `${seg.progressFillPct}%`,
                                              bgcolor: 'rgba(0, 0, 0, 0.3)',
                                              pointerEvents: 'none',
                                            }}
                                          />
                                        )}
                                      </Box>
                                    </Tooltip>
                                  );
                                })}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                    {/* Legend */}
                    {expandedWorkstreams[ws.id] && (
                      <Droppable droppableId={ws.id} direction="horizontal">
                        {(provided) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}
                          >
                            {burndown.map((b, epicIndex) => {
                              const isActive = hoveredEpic?.workstreamId === ws.id && hoveredEpic?.epicId === b.epicId;
                              return (
                                <Draggable key={b.epicId} draggableId={b.epicId} index={epicIndex}>
                                  {(dragProvided) => (
                                    <Box
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                    >
                                      <Box
                                        role="button"
                                        tabIndex={0}
                                        onMouseEnter={() => handleEpicMouseEnter(ws.id, b.epicId)}
                                        onMouseLeave={handleEpicMouseLeave}
                                        onClick={() => navigate(`/epic-planner/${b.epicId}/edit`, { state: { from: '/workstream-forecaster' } })}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(`/epic-planner/${b.epicId}/edit`, { state: { from: '/workstream-forecaster' } }); }}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          bgcolor: b.color,
                                          color: 'white',
                                          fontSize: '0.65rem',
                                          borderRadius: 1,
                                          px: 1,
                                          py: 0.5,
                                          cursor: 'pointer',
                                          outline: isActive ? `2px solid ${getOutlineColor(b.color)}` : 'none',
                                          outlineOffset: '2px',
                                        }}
                                      >
                                        <span
                                          {...dragProvided.dragHandleProps}
                                          role="button"
                                          aria-label="Drag to reorder epic"
                                          tabIndex={0}
                                          style={{ display: 'flex', alignItems: 'center', cursor: 'grab' }}
                                        >
                                          <DragIndicatorIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
                                        </span>
                                        <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit', lineHeight: 1.2 }}>
                                          {b.title}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </Box>
                        )}
                      </Droppable>
                    )}
                  </Paper>
                );
              })}
            </DragDropContext>
          </Box>
        </Box>
      )}
      {/* Timeline config modal */}
      <Dialog open={timelineModalOpen} onClose={() => setTimelineModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Configure Timeline</Typography>
            <IconButton size="small" onClick={() => setTimelineModalOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Start Date"
              type="date"
              value={newStartDate || startDate.toISOString().split('T')[0]}
              onChange={(e) => setNewStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <Box>
              <Typography variant="body2" gutterBottom>Total Sprints</Typography>
              <NumberInput
                value={newTotalSprints}
                onChange={setNewTotalSprints}
                min={1}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <ModalActionFooter
            onCancel={() => setTimelineModalOpen(false)}
            onSave={handleSaveTimeline}
            saveLabel="Apply"
          />
        </DialogActions>
      </Dialog>
      {/* Sprint config modal */}
      {sprintModal.open && (() => {
        const ws = workstreams.find((w) => w.id === sprintModal.workstreamId);
        if (!ws) return null;
        const defaultVelocity = getWorkstreamSprintVelocity(sprintModal.workstreamId, sprintModal.sprintIndex);
        const override = getOverride(sprintModal.workstreamId, sprintModal.sprintIndex);
        return (
          <SprintModal
            open={sprintModal.open}
            workstreamId={sprintModal.workstreamId}
            sprintIndex={sprintModal.sprintIndex}
            sprintDates={sprintDates[sprintModal.sprintIndex]}
            defaultVelocity={defaultVelocity}
            overrideData={override ? { velocity: override.velocity } : undefined}
            onSave={handleSaveSprintOverride}
            onClose={() => setSprintModal({ open: false, workstreamId: '', sprintIndex: 0 })}
          />
        );
      })()}
    </Box>
  );
};

export default WorkstreamForecaster;
